/**
 * Compute active work time on the current card from timer timestamps.
 * No Firestore reads/writes — pure local computation.
 *
 * @param {object|null} timer - The meta/timer document
 * @param {number} [now] - Current timestamp (defaults to Date.now(), pass for consistent renders)
 * @returns {number} milliseconds of active work time on the current card
 */
export function computeCardActiveTime(timer, now) {
  if (!timer || !timer.startedAt) return 0;
  const t = now || Date.now();
  const pause = timer.accumulatedPause || 0;
  if (timer.pauseType && timer.pauseStartedAt) {
    // Paused — freeze at when the pause started
    return Math.max(0, timer.pauseStartedAt - timer.startedAt - pause);
  }
  return Math.max(0, t - timer.startedAt - pause);
}

/**
 * Compute total time on a task: saved timeSpent + live card session.
 */
export function computeTotalTaskTime(task, timer, now) {
  return (task?.timeSpent || 0) + computeCardActiveTime(timer, now);
}

/**
 * Compute elapsed time of the current active pause.
 */
export function computePauseElapsed(timer, now) {
  if (!timer || !timer.pauseType || !timer.pauseStartedAt) return 0;
  const t = now || Date.now();
  return Math.max(0, t - timer.pauseStartedAt);
}

/**
 * Build a clean timer state object.
 */
export function makeTimerState({ taskId = null, startedAt = null, pauseType = null, pauseStartedAt = null, pauseContext = null, accumulatedPause = 0 } = {}) {
  return { taskId, startedAt, pauseType, pauseStartedAt, pauseContext, accumulatedPause };
}

// How much of the running timer may be banked onto `taskId` right now. Zero unless: on the clock,
// the timer started inside this clock session (a timer left over from the last clock-out is not
// work), and the timer is timing this task (or no task yet). `owns` = this call may reset the timer.
export const MAX_BANK_MS = 16 * 3600000;
export function bankable({ timer, clockedIn, taskId, now = Date.now(), allowUnowned = true }) {
  const none = { elapsed: 0, owns: false };
  if (!clockedIn || !timer?.startedAt || timer.startedAt < (clockedIn.startedAt || 0) - 1000) return none;
  if (timer.taskId ? timer.taskId !== taskId : !allowUnowned) return none;
  const elapsed = computeCardActiveTime(timer, now);
  if (!(elapsed > 0) || elapsed > MAX_BANK_MS) return { elapsed: 0, owns: true };
  return { elapsed, owns: true };
}
